import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import Reveal from "@/components/Reveal";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { resolveImg, formatDate } from "@/lib/blog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    axios.get(`${API}/blogs`).then(({ data }) => setPosts(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <main className="bg-konattu-cream min-h-screen" data-testid="blog-page">
      <Seo title="Journal, News, Offers and Insights" description="Latest news, seasonal offers and insights from Konattu Group, Kerala's trusted retail group since 1998." path="/blog" />
      <div className="noise-overlay" />
      <div className="fixed top-0 inset-x-0 z-50 bg-konattu-cream/80 backdrop-blur-2xl border-b border-konattu-charcoal/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-[70px] flex items-center justify-between">
          <Link to="/" data-testid="blog-logo"><Logo /></Link>
          <Link to="/" data-testid="blog-back" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-konattu-charcoal/70 hover:text-konattu-red transition-colors">
            <ArrowLeft size={14} /> BACK TO SITE
          </Link>
        </div>
      </div>

      <section className="pt-[70px]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <span className="font-mono text-xs tracking-[0.35em] text-konattu-red">/ THE JOURNAL</span>
          <h1 className="mt-6 font-display font-extrabold text-konattu-charcoal tracking-tightest leading-[0.9] text-5xl md:text-7xl">
            News, offers & insights.
          </h1>

          {loading ? (
            <p className="mt-16 font-body text-konattu-charcoal/60">Loading...</p>
          ) : posts.length === 0 ? (
            <div className="mt-16 border border-konattu-charcoal/15 p-16 text-center" data-testid="blog-empty">
              <p className="font-body text-konattu-charcoal/60">No posts published yet. Check back soon.</p>
            </div>
          ) : (
            <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-list">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.06}>
                  <Link to={`/blog/${p.slug}`} data-testid="blog-list-card" className="group block border border-konattu-charcoal/12 hover:border-konattu-red/60 transition-colors h-full">
                    <div className="aspect-[16/10] overflow-hidden bg-konattu-sand">
                      {p.cover_image ? (
                        <img src={resolveImg(p.cover_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-konattu-charcoal"><span className="font-display font-bold text-4xl text-konattu-red">K</span></div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3 font-mono text-[10px] tracking-[0.2em] text-konattu-charcoal/50">
                        {p.tags?.[0] && <span className="text-konattu-red">{p.tags[0].toUpperCase()}</span>}
                        <span>{formatDate(p.created_at)}</span>
                      </div>
                      <h2 className="font-display font-semibold text-xl text-konattu-charcoal tracking-tight leading-snug group-hover:text-konattu-red transition-colors">{p.title}</h2>
                      <p className="mt-2 font-body text-sm text-konattu-charcoal/65 leading-relaxed line-clamp-3">{p.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-1 font-mono text-[11px] tracking-[0.2em] text-konattu-red">READ <ArrowUpRight size={12} /></span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
